/* save-plan.js — /audio-metadata: «что именно будет сохранено».
 *
 * Страница редактора тегов держит ОДНУ запись на трек: исходные байты, правки
 * полей, действие с обложкой и — с 2026-10-06 — контейнер, в котором человек
 * попросил сохранить дорожку. Всё, что зависит от этого состояния (видна ли
 * строка «Сохранить и скачать», какие контейнеры предложить, как назвать
 * файл), считается здесь, чистыми функциями, и проверяется в Node
 * (tests/js/audio-metadata-save-plan.test.mjs).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. Чат поддержки 574 (05.10.2026, Galaxy S25 Ultra):
 * «Я сделал все изменения в метаданных. Решил сохранить во FLAC, но не могу
 * скачать. Кнопка просто исчезает». Кнопка формата тогда конвертировала запись
 * на месте и перерисовывала карточку, а строку сохранения показывал отдельный
 * вызов, о котором перерисовка «забыла»: правки на месте, кнопки нет. Замер по
 * проду за три недели: 177 сессий нажали на предложение формата, 63 из них
 * (36 %) после этого не сохранили ничего. Видимость строки была событием, а
 * должна быть функцией состояния — она здесь (`signature` / `rowMode`).
 *
 * Тем же замером нашлось, что предлагать можно не всё подряд — см.
 * `containersFor`.
 */
(function (root, factory) {
    'use strict';
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.AmSavePlan = mod;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* Контейнеры, в которые страница умеет пересохранить дорожку, и кодек,
     * которым это делает её FFmpeg. `maxChannels` — ЗАМЕР 2026-10-06 на сборке
     * страницы (Lavf 59.27), источник — WAV с раскладками 3.0 / 3.1 / 5.1 /
     * 5.1(side) / 7.1, оракул — ffprobe:
     *
     *            FLAC            MP3              M4A (AAC)
     *   3.0      3.0             stereo           3.0
     *   3.1      3.1             stereo           4 канала, раскладка unknown
     *   5.1      5.1             stereo           5.1
     *   5.1(side) 5.1(side)      stereo           6 каналов, раскладка unknown
     *   7.1      7.1             stereo           7.1
     *
     * MP3 молча сводит всё в стерео; AAC сохраняет число каналов, но у части
     * раскладок теряет смысл каналов — а какая у файла раскладка, страница не
     * читает. Поэтому многоканальной дорожке предлагается только FLAC: файл с
     * именем «…_31.mp3» и двумя каналами внутри — та же ловушка, что уже
     * закрывали в «Конвертере каналов» (он и делает такие WAV).
     */
    var CONTAINERS = {
        flac: { codec: ['-c:a', 'flac'], maxChannels: 8 },
        mp3: { codec: ['-c:a', 'libmp3lame', '-q:a', '2'], maxChannels: 2 },
        m4a: { codec: ['-c:a', 'aac', '-b:a', '192k'], maxChannels: 2 }
    };
    var ORDER = ['flac', 'mp3', 'm4a'];

    /**
     * В какие контейнеры можно пересохранить ЭТУ дорожку.
     *   audio = { channels, bitDepth } — что известно о звуке (0/undefined = неизвестно)
     *   → [{ fmt, lossless }]
     *
     * `lossless` — можно ли честно написать «без потерь». Замер там же: FLAC
     * этой сборки пишет не больше 24 бит, поэтому 16- и 24-битный целый PCM
     * возвращается тем же PCM (md5 совпал), а 32-битный целый и float — уже
     * нет. Разрядность неизвестна — обещания нет.
     */
    function containersFor(audio) {
        var ch = Math.max(0, Math.floor(Number(audio && audio.channels) || 0));
        var bits = Math.max(0, Math.floor(Number(audio && audio.bitDepth) || 0));
        var out = [];
        for (var i = 0; i < ORDER.length; i++) {
            var f = ORDER[i];
            if (ch && ch > CONTAINERS[f].maxChannels) continue;
            out.push({ fmt: f, lossless: f === 'flac' && bits > 0 && bits <= 24 });
        }
        return out;
    }

    /** Аргументы кодека для контейнера (копия — вызывающий дописывает свои). */
    function codecArgs(fmt) {
        var c = CONTAINERS[fmt];
        if (!c) throw new Error('save_plan_unknown_container:' + fmt);
        return c.codec.slice();
    }

    /* КАНАЛЫ НА СВОИХ МЕСТАХ. У контейнера есть раскладка, которую он
     * подразумевает по одному ЧИСЛУ каналов, и железо играет именно её: тег с
     * маской каналов ресиверы не читают. Четыре канала FLAC — это квадро
     * (FL FR BL BR), и FFmpeg говорит об этом сам: «Channel layout not
     * supported by Flac, output stream will have incorrect channel layout».
     * Дорожка 3.1 (FL FR FC LFE) в четырёхканальном FLAC на ресивере играет
     * центр из левого тыла, а сабвуфер из правого — так это услышал человек на
     * настоящей системе (16.07.2026, «Конвертер каналов»), и с тех пор
     * конвертер отдаёт 3.1 во FLAC внутри контейнера 5.1 с пустыми тыловыми.
     *
     * Эта страница 06.10.2026 сохранила при пересохранении только тег маски:
     * ffprobe читал «3.1», и на этом проверка кончалась. Формально верно, на
     * железе — по-прежнему квадро. Ответ «уже решали» лежал в соседнем туле;
     * здесь он тот же.
     *
     * Замер 07.10.2026 (FFmpeg 7.0 и сборка страницы): `pan` канал-в-канал —
     * «Pure channel mapping», первые четыре канала побайтно прежние (md5 PCM),
     * тыловые — цифровая тишина, файл больше на ~300 байт.
     */
    var MASK_31 = 0x0F;                     // FL | FR | FC | LFE — маска WAVE_FORMAT_EXTENSIBLE

    /**
     * Как положить дорожку в контейнер, чтобы каналы стояли там, где их ждёт плеер.
     *   audio = { channels, channelMask }   (маска 0/undefined — неизвестна)
     *   → null — как есть; иначе { from, layout, args } — раскладка источника, раскладка
     *     в файле и аргументы FFmpeg, которые её дают
     */
    function repack(fmt, audio) {
        var ch = Math.floor(Number(audio && audio.channels) || 0);
        var mask = Math.floor(Number(audio && audio.channelMask) || 0);
        if (fmt === 'flac' && ch === 4 && mask === MASK_31) {
            return { from: '3.1', layout: '5.1', args: ['-af', 'pan=5.1|c0=c0|c1=c1|c2=c2|c3=c3'] };
        }
        return null;
    }

    /** «a.b.wav» + flac → «a.b.flac»; имя без расширения получает его. */
    function withExt(name, ext) {
        var s = String(name == null ? '' : name), e = String(ext || '').replace(/^\./, '');
        if (!e) return s;
        var m = s.match(/^(.*)\.([A-Za-z0-9]{1,8})$/);
        return (m ? m[1] : s) + '.' + e;
    }

    /**
     * Отпечаток того, что запишет сохранение, — строка. Две записи с одним
     * отпечатком дадут один и тот же файл, поэтому «есть ли несохранённое»
     * = «отпечаток отличается от отпечатка последней выдачи (или загрузки)».
     *   plan = { set: {поле: значение}, cover: 'keep'|'none'|'new:<id>', fmt, name }
     */
    function signature(plan) {
        plan = plan || {};
        var set = plan.set || {}, keys = [], k;
        for (k in set) {
            if (!Object.prototype.hasOwnProperty.call(set, k)) continue;
            if (set[k] === undefined || set[k] === null || String(set[k]).trim() === '') continue;
            keys.push(k);
        }
        keys.sort();
        var parts = [];
        for (var i = 0; i < keys.length; i++) parts.push(keys[i] + '=' + String(set[keys[i]]).trim());
        return JSON.stringify([parts, String(plan.cover || 'keep'), String(plan.fmt || ''), String(plan.name || '')]);
    }

    /**
     * Что показывает строка сохранения:
     *   'edit'  — есть что сохранять: имя файла и «Сохранить и скачать»;
     *   'saved' — сохранять нечего, а выданный файл есть: «Сохранено как …» и «Скачать»;
     *   'idle'  — ни того, ни другого.
     */
    function rowMode(dirty, hasResult) {
        return dirty ? 'edit' : (hasResult ? 'saved' : 'idle');
    }

    return {
        CONTAINERS: CONTAINERS,
        containersFor: containersFor,
        codecArgs: codecArgs,
        repack: repack,
        withExt: withExt,
        signature: signature,
        rowMode: rowMode
    };
});
